import { Component, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TypeOfCallSopInputVesselStatusMappingService } from 'source/app/proxy/type-of-calls';
import { projectVesselStatusOptions } from 'source/app/proxy/projects';
import { PagedRequestDto } from 'source/app/xui/x-page/x-pg-model/x-pg.model';

@Component({
  selector: 'app-type-of-call-vessel-status-mapping',
  templateUrl: './type-of-call-vessel-status-mapping.component.html',
  styleUrls: ['./type-of-call-vessel-status-mapping.component.scss']
})
export class TypeOfCallVesselStatusMappingComponent implements OnInit {

  searchValue = '';
  showColumnFilters = false;
  columnFilters: { [key: string]: string } = {};
  isEditMode = false;
  isVisible = false;
  isOkLoading = false;
  
  data: any[] = [];
  filteredData: any[] = [];
  
  tableHeight = 535;
  typeOfCallId: string;
  
  vesselStatusOptions = [];
  allVesselStatusOptions = projectVesselStatusOptions;

  sopInputs = [];
  
  vesselStatusForm: FormGroup;
  selectedMapping: any = null;

  constructor(
    private inj: Injector,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private nzMessageService: NzMessageService,
    private typeOfCallSopInputVesselStatusMappingService: TypeOfCallSopInputVesselStatusMappingService
  ) {
    this.route.queryParamMap.subscribe(params => {
      this.typeOfCallId = this.route.parent?.snapshot.params.id;
    });
  }

  ngOnInit(): void {
    this.updateTableHeight();
    this.initializeForm();
    this.getVesselStatusList();
    this.getSopInputs();
  }

  initializeForm() {
    this.vesselStatusForm = this.formBuilder.group({
      typeOfCallId: [this.typeOfCallId],
      status: [null, Validators.required],
      sopInputIds: [[], Validators.required],
    });
  }

  private filterVesselStatusOptions(currentStatus?: number): void {
  const usedStatuses = new Set(
    this.data
      .filter(d => d.status !== currentStatus) // 👈 allow current status
      .map(d => d.status)
  );
  this.vesselStatusOptions = this.allVesselStatusOptions.filter(
    option => !usedStatuses.has(option.value)
  );
}


  // Open modal for creating new mapping
  openModal(): void {
    this.isEditMode = false;
    this.selectedMapping = null;
    this.vesselStatusForm.reset({
      typeOfCallId: this.typeOfCallId,
      status: null,
      sopInputIds: [],
    });
    this.isVisible = true;
    this.vesselStatusForm.get('status')?.enable();
  }

  get isCreateDisabled(){
    return !(this.data.length < 4); 
  }

  getVesselStatusList() {
    this.typeOfCallSopInputVesselStatusMappingService.getList({
      filter: `typeOfCallId eq "${this.typeOfCallId}"`,
      ignoreMaxResultCount: true,
    } as PagedRequestDto).subscribe(response => {
      
      this.data = response.items.map(item => ({
        id: item.id,
        status: item.status,
        statusLabel: this.getStatusLabel(item.status),
        sopInputIds: item.sopInputIds || [],
        sopInputTitles: item.sopInputTitles || [],
        sopInputDisplay: this.formatSopInputs(item.sopInputTitles),
        concurrencyStamp: item.concurrencyStamp
      }));
      this.filterVesselStatusOptions();
      this.filteredData = [...this.data];
    });
  }

  getSopInputs() {
    this.typeOfCallSopInputVesselStatusMappingService.getSopInputs(this.typeOfCallId).subscribe(response => {
      this.sopInputs = response;
    }, error => {
      console.error('Error fetching SOP inputs:', error);
    });
  }

  getStatusLabel(statusValue: number): string {
    const status = this.allVesselStatusOptions.find(s => s.value === statusValue);
    return status ? status.key : 'Unknown';
  }

  formatSopInputs(titles: string[]): string {
    if (!titles || titles.length === 0) {
      return 'No SOP Inputs';
    }
    return titles.join(', ');
  }

  onSearch(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase().trim();
    this.searchValue = searchTerm;
    this.applyFilters();
  }

  toggleColumnFilters(): void {
    this.showColumnFilters = !this.showColumnFilters;
    if (!this.showColumnFilters) {
      this.columnFilters = {};
      this.applyFilters();
    }
  }

  updateColumnFilter(column: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase().trim();

    if (value) {
      this.columnFilters[column] = value;
    } else {
      delete this.columnFilters[column];
    }

    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.data];

    // Apply global search
    if (this.searchValue) {
      filtered = filtered.filter(item =>
        (item.statusLabel?.toLowerCase() || '').includes(this.searchValue) ||
        (item.sopInputDisplay?.toLowerCase() || '').includes(this.searchValue)
      );
    }

    // Apply column filters
    Object.keys(this.columnFilters).forEach(column => {
      const filterValue = this.columnFilters[column];

      switch (column) {
        case 'status':
          filtered = filtered.filter(item =>
            (item.statusLabel?.toLowerCase() || '').includes(filterValue)
          );
          break;
        case 'sopInputs':
          filtered = filtered.filter(item =>
            (item.sopInputDisplay?.toLowerCase() || '').includes(filterValue)
          );
          break;
      }
    });

    this.filteredData = filtered;
  }

  editMapping(id: string): void {
    const mapping = this.data.find(item => item.id === id);
    
    if (!mapping) {
      this.nzMessageService.error('Vessel status mapping not found');
      return;
    }

    this.isEditMode = true;
    this.selectedMapping = mapping;
    
    this.vesselStatusForm.patchValue({
      typeOfCallId: this.typeOfCallId,
      status: mapping.status,
      sopInputIds: mapping.sopInputIds || [],
    });
    this.filterVesselStatusOptions(mapping.status);

    this.vesselStatusForm.markAsPristine();
    this.isVisible = true;
  }

  saveMapping(): void {    
    if (this.vesselStatusForm.invalid) {
      this.nzMessageService.error('Please fill all required fields!');
      return;
    }

    const formValues = this.vesselStatusForm.value;
    
    // Validate that at least one SOP input is selected
    if (!formValues.sopInputIds || formValues.sopInputIds.length === 0) {
      this.nzMessageService.error('Please select at least one SOP Input');
      return;
    }

    this.isOkLoading = true;
    const input = {
      typeOfCallId: formValues.typeOfCallId,
      status: formValues.status,
      sopInputIds: formValues.sopInputIds,
    };

    this.typeOfCallSopInputVesselStatusMappingService.create(input).subscribe({
      next: () => {
        this.nzMessageService.success('Vessel status mapping added successfully!');
        this.isVisible = false;
        this.isOkLoading = false;
        this.getVesselStatusList();
      },
      error: (error) => {
        this.isOkLoading = false;
        const backendMessage = error?.error?.error?.message || 'Failed to add vessel status mapping';
        this.nzMessageService.error(backendMessage);
      },
    });
  }

  updateMapping(): void {
    if (this.vesselStatusForm.valid && this.selectedMapping) {
      const formValues = this.vesselStatusForm.value;
      
      // Validate that at least one SOP input is selected
      if (!formValues.sopInputIds || formValues.sopInputIds.length === 0) {
        this.nzMessageService.error('Please select at least one SOP Input');
        return;
      }

      this.isOkLoading = true;

      const updateDto = {
        typeOfCallId: formValues.typeOfCallId,
        status: formValues.status,
        sopInputIds: formValues.sopInputIds,
        concurrencyStamp: this.selectedMapping.concurrencyStamp,
      };

      this.typeOfCallSopInputVesselStatusMappingService
        .update(this.selectedMapping.id, updateDto)
        .subscribe({
          next: (response) => {
            this.nzMessageService.success('Vessel status mapping updated successfully.');
            this.isVisible = false;
            this.isOkLoading = false;
            this.getVesselStatusList();
          },
          error: (error) => {
            this.isOkLoading = false;
            const backendMessage = error?.error?.error?.message || 'Failed to update vessel status mapping';
            this.nzMessageService.error(backendMessage);
            console.error(`Failed to update: ${error.status} ${error.statusText}`);
          },
        });
    } else {
      this.nzMessageService.error('Please fill all required fields!');
    }
  }

  deleteMapping(id: string): void {
    if (!id) {
      this.nzMessageService.error('Invalid mapping ID');
      return;
    }

    this.typeOfCallSopInputVesselStatusMappingService.delete(id).subscribe({
      next: () => {
        this.data = this.data.filter(item => item.id !== id);
        this.filterVesselStatusOptions();
        this.applyFilters();
        this.nzMessageService.success('Vessel status mapping deleted successfully');
      },
      error: (error) => {
        this.nzMessageService.error('Failed to delete vessel status mapping');
        console.error('Error deleting vessel status mapping:', error);
      },
    });
  }

  handleSave(): void {
    if (this.isEditMode && this.selectedMapping) {
      this.updateMapping();
    } else {
      this.saveMapping();
    }
  }

  handleCancel(): void {
    this.isVisible = false;
    this.isEditMode = false;
    this.selectedMapping = null;
    this.isOkLoading = false;
    this.vesselStatusForm.reset({
      typeOfCallId: this.typeOfCallId,
      status: null,
      sopInputIds: [],
    });
  }

  get isSaveDisabled(): boolean {
    return this.vesselStatusForm.invalid || this.isOkLoading;
  }


  updateTableHeight() {
    const screenHeight = window.innerHeight;

    if (screenHeight < 1080) {
      this.tableHeight = 535;
    } else if (screenHeight > 1080) {
      this.tableHeight = 750;
    }
  }

  // Sort functions
  sortStatus = (a: any, b: any) => (a.statusLabel || '').localeCompare(b.statusLabel || '');
  sortSopInputs = (a: any, b: any) => (a.sopInputDisplay || '').localeCompare(b.sopInputDisplay || '');
}